import React, { useEffect, useState } from "react";
import { getOrCreateTableOrder } from "../api";
import {
  getTables,
  createTable,
  updateTable,
  deleteTable,
  updateTableStatus,
} from "../api";

import TableCard from "./TableCard";
import TableModal from "./TableModal";

export default function TablesView({ onOpenPOS }) {

  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [editingTable, setEditingTable] = useState(null);

  const emptyTable = {
    tableName: "",
    capacity: 4,
    notes: "",
  };

  const [tableForm, setTableForm] = useState(emptyTable);

  //-----------------------------------
  // Load Tables
  //-----------------------------------

  const loadTables = async () => {
    try {
      setLoading(true);

      const data = await getTables();

      setTables(data);
      setFilteredTables(data);
    } catch (err) {
      console.error(err);
      alert("Unable to load restaurant tables.");
    } finally {
      setLoading(false);
    }
  };

const handleOpenTable = async (table) => {
  try {
    const order = await getOrCreateTableOrder(table.id);

    onOpenPOS(table, order);

  } catch (err) {
    console.error(err);
    alert("Unable to open table.");
  }
};

  useEffect(() => {
    loadTables();
  }, []);

  //-----------------------------------
  // Search
  //-----------------------------------

  useEffect(() => {
    const keyword = search.toLowerCase();

    setFilteredTables(
      tables.filter((t) => {
        return (
          t.tableName.toLowerCase().includes(keyword) ||
          (t.notes || "").toLowerCase().includes(keyword)
        );
      }),
    );
  }, [search, tables]);

  //-----------------------------------
  // Add New
  //-----------------------------------

  const handleAdd = () => {
    setEditingTable(null);

    setTableForm(emptyTable);

    setShowModal(true);
  };

  //-----------------------------------
  // Edit
  //-----------------------------------

  const handleEdit = (table) => {
    setEditingTable(table);

    setTableForm({
      tableName: table.tableName,
      capacity: table.capacity,
      notes: table.notes || "",
    });

    setShowModal(true);
  };

  //-----------------------------------
  // Save
  //-----------------------------------

  const handleSave = async () => {
    try {
      if (!tableForm.tableName.trim()) {
        alert("Enter table name.");
        return;
      }

      if (editingTable) {
        await updateTable(editingTable.id, tableForm);
      } else {
        await createTable(tableForm);
      }

      setShowModal(false);

      setEditingTable(null);

      setTableForm(emptyTable);

      loadTables();
    } catch (err) {
      console.error(err);
      alert("Unable to save table.");
    }
  };

  //-----------------------------------
  // Delete
  //-----------------------------------

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this table?");

    if (!ok) return;

    try {
      await deleteTable(id);

      loadTables();
    } catch (err) {
      console.error(err);

      alert("Unable to delete.");
    }
  };

  //-----------------------------------
  // Status Change
  //-----------------------------------

  const handleStatusChange = async (id, status) => {
    try {
      await updateTableStatus(id, status);

      loadTables();
    } catch (err) {
      console.error(err);

      alert("Unable to update status.");
    }
  };

  //-----------------------------------
  // JSX
  //-----------------------------------

  return (
    <div className="so-tables-view p-6 h-full overflow-auto">

      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-tables-view {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-tables-view * {
          box-sizing: border-box;
        }

        .so-tables-title {
          font-family: var(--font-display);
          font-size: 2rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-tables-subtitle {
          color: var(--stone);
          margin-top: 4px;
        }

        .so-tables-add-btn {
          background: var(--chili);
          color: var(--paper);
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-tables-add-btn:hover {
          background: var(--chili-dark);
        }

        .so-tables-search-wrap {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 10px;
        }

        .so-tables-search-input {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 10px 14px;
          background: var(--paper);
          color: var(--ink);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-tables-search-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-tables-empty {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
        }

        .so-tables-empty-title {
          font-family: var(--font-display);
          font-size: 1.4rem;
          color: var(--ink);
        }
      `}</style>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="so-tables-title">
            Restaurant Tables
          </h2>

          <p className="so-tables-subtitle">Manage dining tables</p>
        </div>

        <button
          onClick={handleAdd}
          className="so-tables-add-btn px-5 py-2 rounded-lg"
        >
          + Add Table
        </button>
      </div>

      <div className="so-tables-search-wrap p-4 mb-6">
        <input
          type="text"
          placeholder="Search table..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="so-tables-search-input"
        />
      </div>

      {loading ? (
        <div className="text-center py-20" style={{ color: "var(--stone)" }}>Loading...</div>
      ) : filteredTables.length === 0 ? (
        <div className="so-tables-empty p-12 text-center">
          <h3 className="so-tables-empty-title">No Tables Found</h3>

          <p className="so-tables-subtitle mt-2">
            Click "Add Table" to create one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTables.map((table) => (
            <TableCard
              key={table.id}
              table={table}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
              onOpen={handleOpenTable}
            />
          ))}
        </div>
      )}

      <TableModal
        open={showModal}
        onClose={() => setShowModal(false)}
        tableForm={tableForm}
        setTableForm={setTableForm}
        onSave={handleSave}
        editing={editingTable}
      />
    </div>
  );
}










// import React, { useEffect, useState } from "react";
// import { getOrCreateTableOrder } from "../api";
// import {
//   getTables,
//   createTable,
//   updateTable,
//   deleteTable,
//   updateTableStatus,
// } from "../api";

// import TableCard from "./TableCard";
// import TableModal from "./TableModal";

// export default function TablesView({ onOpenPOS }) {
  
//   const [tables, setTables] = useState([]);
//   const [filteredTables, setFilteredTables] = useState([]);

//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");

//   const [showModal, setShowModal] = useState(false);

//   const [editingTable, setEditingTable] = useState(null);

//   const emptyTable = {
//     tableName: "",
//     capacity: 4,
//     notes: "",
//   };

//   const [tableForm, setTableForm] = useState(emptyTable);

//   //-----------------------------------
//   // Load Tables
//   //-----------------------------------

//   const loadTables = async () => {
//     try {
//       setLoading(true);

//       const data = await getTables();

//       setTables(data);
//       setFilteredTables(data);
//     } catch (err) {
//       console.error(err);
//       alert("Unable to load restaurant tables.");
//     } finally {
//       setLoading(false);
//     }
//   };

// const handleOpenTable = async (table) => {
//   try {
//     const order = await getOrCreateTableOrder(table.id);

//     onOpenPOS(table, order);

//   } catch (err) {
//     console.error(err);
//     alert("Unable to open table.");
//   }
// };

//   useEffect(() => {
//     loadTables();
//   }, []);

//   //-----------------------------------
//   // Search
//   //-----------------------------------

//   useEffect(() => {
//     const keyword = search.toLowerCase();

//     setFilteredTables(
//       tables.filter((t) => {
//         return (
//           t.tableName.toLowerCase().includes(keyword) ||
//           (t.notes || "").toLowerCase().includes(keyword)
//         );
//       }),
//     );
//   }, [search, tables]);

//   //-----------------------------------
//   // Add New
//   //-----------------------------------

//   const handleAdd = () => {
//     setEditingTable(null);

//     setTableForm(emptyTable);

//     setShowModal(true);
//   };

//   //-----------------------------------
//   // Edit
//   //-----------------------------------

//   const handleEdit = (table) => {
//     setEditingTable(table);

//     setTableForm({
//       tableName: table.tableName,
//       capacity: table.capacity,
//       notes: table.notes || "",
//     });

//     setShowModal(true);
//   };

//   //-----------------------------------
//   // Save
//   //-----------------------------------

//   const handleSave = async () => {
//     try {
//       if (!tableForm.tableName.trim()) {
//         alert("Enter table name.");
//         return;
//       }

//       if (editingTable) {
//         await updateTable(editingTable.id, tableForm);
//       } else {
//         await createTable(tableForm);
//       }

//       setShowModal(false);

//       setEditingTable(null);

//       setTableForm(emptyTable);

//       loadTables();
//     } catch (err) {
//       console.error(err);
//       alert("Unable to save table.");
//     }
//   };

//   //-----------------------------------
//   // Delete
//   //-----------------------------------

//   const handleDelete = async (id) => {
//     const ok = window.confirm("Delete this table?");

//     if (!ok) return;

//     try {
//       await deleteTable(id);

//       loadTables();
//     } catch (err) {
//       console.error(err);

//       alert("Unable to delete.");
//     }
//   };

//   //-----------------------------------
//   // Status Change
//   //-----------------------------------

//   const handleStatusChange = async (id, status) => {
//     try {
//       await updateTableStatus(id, status);

//       loadTables();
//     } catch (err) {
//       console.error(err);

//       alert("Unable to update status.");
//     }
//   };

//   //-----------------------------------
//   // JSX
//   //-----------------------------------

//   return (
//     <div className="p-6 h-full overflow-auto">
//       <div className="flex justify-between items-center mb-6">
//         <div>
//           <h2 className="text-3xl font-bold text-slate-800">
//             Restaurant Tables
//           </h2>

//           <p className="text-slate-500 mt-1">Manage dining tables</p>
//         </div>

//         <button
//           onClick={handleAdd}
//           className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow"
//         >
//           + Add Table
//         </button>
//       </div>

//       <div className="bg-white rounded-lg shadow p-4 mb-6">
//         <input
//           type="text"
//           placeholder="Search table..."
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//           className="w-full border rounded-lg px-4 py-2"
//         />
//       </div>

//       {loading ? (
//         <div className="text-center py-20 text-slate-500">Loading...</div>
//       ) : filteredTables.length === 0 ? (
//         <div className="bg-white rounded-lg shadow p-12 text-center">
//           <h3 className="text-xl font-semibold">No Tables Found</h3>

//           <p className="text-slate-500 mt-2">
//             Click "Add Table" to create one.
//           </p>
//         </div>
//       ) : (
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
//           {filteredTables.map((table) => (
//             <TableCard
//               key={table.id}
//               table={table}
//               onEdit={handleEdit}
//               onDelete={handleDelete}
//               onStatusChange={handleStatusChange}
//               onOpen={handleOpenTable}
//             />
//           ))}
//         </div>
//       )}

//       <TableModal
//         open={showModal}
//         onClose={() => setShowModal(false)}
//         tableForm={tableForm}
//         setTableForm={setTableForm}
//         onSave={handleSave}
//         editing={editingTable}
//       />
//     </div>
//   );
// }
